import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

function buildVersionUrl(): string {
    const base = import.meta.env.BASE_URL || '/';
    const prefix = base.endsWith('/') ? base : `${base}/`;
    return `${prefix}version.json`.replace(/([^:]\/)\/+/g, '$1');
}

type FetchMetaResult =
    | { kind: 'ok'; buildId: string }
    | { kind: 'missing' }
    | { kind: 'unavailable' };

async function fetchRemoteBuildMeta(): Promise<FetchMetaResult> {
    try {
        const url = `${buildVersionUrl()}?t=${Date.now()}`;
        const res = await fetch(url, {
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        });
        if (res.status === 404) {
            return { kind: 'missing' };
        }
        if (!res.ok) {
            return { kind: 'unavailable' };
        }
        const data = (await res.json()) as { buildId?: string };
        if (typeof data.buildId === 'string' && data.buildId.length > 0) {
            return { kind: 'ok', buildId: data.buildId };
        }
        return { kind: 'unavailable' };
    } catch {
        return { kind: 'unavailable' };
    }
}

/**
 * Polls /version.json (no-store) and compares to the build id baked into this bundle.
 * When a new deployment is published, prompts the user to reload so the SPA picks up new hashed chunks.
 *
 * Disabled in dev (no version.json on Vite dev server). Stops polling if version.json returns 404
 * (e.g. static host without the file).
 */
const DeploymentUpdateNotifier: React.FC = () => {
    const localBuildId = import.meta.env.VITE_BUILD_ID;
    const [open, setOpen] = useState(false);
    const dismissedRef = useRef(false);
    const pollingStoppedRef = useRef(false);
    const intervalRef = useRef<number | null>(null);

    const clearPollInterval = useCallback(() => {
        if (intervalRef.current != null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const check = useCallback(async () => {
        if (import.meta.env.DEV || pollingStoppedRef.current || dismissedRef.current || document.visibilityState === 'hidden') {
            return;
        }
        const meta = await fetchRemoteBuildMeta();
        if (meta.kind === 'missing') {
            pollingStoppedRef.current = true;
            clearPollInterval();
            return;
        }
        if (meta.kind === 'unavailable') {
            return;
        }
        if (meta.buildId === localBuildId) {
            return;
        }
        setOpen(true);
    }, [clearPollInterval, localBuildId]);

    useEffect(() => {
        if (import.meta.env.DEV || !localBuildId) {
            return undefined;
        }
        intervalRef.current = window.setInterval(check, POLL_INTERVAL_MS);
        const onFocus = () => {
            void check();
        };
        const onVis = () => {
            if (document.visibilityState === 'visible') {
                void check();
            }
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVis);
        void check();
        return () => {
            clearPollInterval();
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [check, clearPollInterval, localBuildId]);

    const handleReload = () => {
        window.location.reload();
    };

    const handleDismiss = () => {
        dismissedRef.current = true;
        setOpen(false);
    };

    if (import.meta.env.DEV || !localBuildId) {
        return null;
    }

    return (
        <Snackbar open={open} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ maxWidth: 480 }}>
            <Alert
                severity="info"
                variant="filled"
                onClose={handleDismiss}
                action={
                    <>
                        <Button color="inherit" size="small" onClick={handleReload}>
                            Reload
                        </Button>
                        <Button color="inherit" size="small" onClick={handleDismiss}>
                            Later
                        </Button>
                    </>
                }
            >
                A new version of the application is available. Reload to get the latest updates.
            </Alert>
        </Snackbar>
    );
};

export default DeploymentUpdateNotifier;
